import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableHead,
  TableRow,
  Box,
  TablePagination,
  CircularProgress,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { formatarDataBrasilia } from "../../../utils/formatDate";
import { getAuditarUsuarios, getIdTenantConfig } from "../../../services/api";

const colunas = [
  { value: "id_usuarios", label: "ID" },
  { value: "nome", label: "Nome" },
  { value: "email", label: "Email" },
  { value: "projeto", label: "Projeto" },
  { value: "permissao", label: "Permissão" },
  { value: "data_criacao", label: "Data de Criação" },
  { value: "data_modificacao", label: "Data de Modificação" },
];

export default function TableComponent() {
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [colunaSelecionada, setColunaSelecionada] = useState("");
  const [filtroColuna, setFiltroColuna] = useState("");

  const normalizeWord = (text: string) => text.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isValidColumnSearch = () => {
    return colunaSelecionada && filtroColuna;
  }

  const handleColumnSearch = () => {
    if (!isValidColumnSearch()) return;
  
    const filtered = data.filter(row => {
      const value = row[colunaSelecionada];
  
      if (value == null) return false;
  
      const normalizedFilter = normalizeWord(filtroColuna);
  
      if (colunaSelecionada === "id_usuarios") {
        return value.toString() === filtroColuna;
      }
  
      if (
        ["data_criacao", "data_modificacao", "data_tentativa"].includes(colunaSelecionada)
      ) {
        const formattedValue = normalizeWord(formatarDataBrasilia(value));
        return formattedValue.includes(normalizedFilter);
      }
  
      const normalizedValue = normalizeWord(value.toString());
      return normalizedValue.includes(normalizedFilter);
    });
  
    setFilteredData(filtered);
    setPage(0);
  }
  

  const clearColumnFilters = () => {
    setColunaSelecionada("");
    setFiltroColuna("");
    setFilteredData(data);
    setPage(0);
  }

  useEffect(() => {
    const fetch = async () => {
      try {
        setIsLoading(true);
        const tenantId = await getIdTenantConfig();
        if (!tenantId) {
          throw new Error("Tenant não identificado");
        }
        const resp = await getAuditarUsuarios({ id_tenants: tenantId });
        const arr = Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : [];
        setData(arr);
        setFilteredData(arr);
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, []);


  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={2} textAlign="center">
        Erro ao carregar os dados: {error.message}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "90%", margin: "auto" }}>
      <Box sx={{ display: "flex", gap: 2, marginBottom: 3, alignItems: "center" }}>
        <FormControl variant="outlined" sx={{ minWidth: 200 }}>
          <InputLabel id="select-coluna">Coluna</InputLabel>
          <Select
            labelId="select-coluna"
            value={colunaSelecionada}
            onChange={(e) => setColunaSelecionada(e.target.value)}
            label="Coluna"
          >
            {colunas.map((coluna) => (
              <MenuItem key={coluna.value} value={coluna.value}>
                {coluna.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Filtro"
          variant="outlined"
          value={filtroColuna}
          onChange={(e) => setFiltroColuna(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleColumnSearch()}
          sx={{ flex: 1, minWidth: 200 }}
        />

        <Button
          color="success"
          onClick={handleColumnSearch}
          sx={{ width: "143.7px", height: "56px" }}
          variant="contained"
          disabled={!isValidColumnSearch()}
        >
          Filtrar
        </Button>

        <Button
          color="warning"
          onClick={clearColumnFilters}
          sx={{ width: "143.7px", height: "56px" }}
          variant="contained"
        >
          Limpar Filtros
        </Button>
      </Box>

      <Table sx={{ minWidth: 750, borderRadius: "8px" }}>
        <TableHead>
          <TableRow>
            {colunas.map((coluna) => (
              <TableCell key={coluna.value} sx={{ padding: "16px" }}>
                <TableSortLabel>{coluna.label}</TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {(Array.isArray(filteredData) ? filteredData : [])
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, index) => (
            <TableRow key={index}>
              {colunas.map((coluna) => (
                <TableCell key={coluna.value} sx={{ padding: "16px" }}>
                  {coluna.value === "sucesso"
                    ? row[coluna.value] === true
                      ? "Sim"
                      : "Não"
                    : coluna.value === "data_tentativa" || coluna.value === "data_criacao" || coluna.value === "data_modificacao"
                    ? formatarDataBrasilia(row[coluna.value])
                    : row[coluna.value]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        labelRowsPerPage={"Linhas por página"}
        count={(Array.isArray(filteredData) ? filteredData : []).length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{ marginTop: 2 }}
      />

      {(Array.isArray(filteredData) ? filteredData : []).length === 0 && (
        <Box sx={{ textAlign: "center", marginTop: 2 }}>
          <h3>Nenhum item encontrado para o filtro aplicado</h3>
        </Box>
      )}
    </Box>
  );
}
